<template>
    <DialogRoot
        v-model:open="open"
        :modal="false"
        :unmount-on-hide="unmountWhenHidden"
    >
        <slot
            name="trigger"
            :open="openModal"
        />

        <DialogPortal>
            <Transition name="mp-overlay">
                <div
                    v-if="showOverlay && open"
                    class="fixed inset-0 z-50 bg-gray-500/75"
                    @click.self="onOverlayClick"
                />
            </Transition>

            <!--
                No `@after-leave`: reka keeps this element mounted, so the leave never runs. It
                restores focus to the opener itself when `present` flips false, hidden or unmounted.
            -->
            <Transition :name="transitionName">
                <DialogContent
                    :class="contentClasses"
                    @escape-key-down="onEscapeKeyDown"
                    @pointer-down-outside="(e: any) => e.preventDefault()"
                    @interact-outside="(e: any) => e.preventDefault()"
                >
                    <div class="flex min-h-0 flex-1 flex-col">
                        <!-- Header -->
                        <div class="flex shrink-0 items-center border-b px-4 py-3 sm:px-6">
                            <button
                                v-if="closeButton"
                                type="button"
                                class="mr-4 flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 focus:outline-hidden md:hidden"
                                @click="requestClose"
                            >
                                <SvgIcon
                                    name="system-forward"
                                    class="h-5 w-5 rotate-180 text-gray-900"
                                />
                            </button>

                            <slot
                                name="header"
                                :form-id="formId"
                            >
                                <DialogTitle
                                    v-if="title"
                                    as="h3"
                                    class="text-lg font-medium"
                                >
                                    {{ title }}
                                </DialogTitle>
                            </slot>

                            <!--
                                A plain button rather than reka's DialogClose: that writes the model
                                directly, and a retaining modal's X has to check for unsaved changes first.
                            -->
                            <button
                                v-if="closeButton"
                                type="button"
                                class="ml-auto hidden h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 focus:outline-hidden md:flex"
                                data-test="mp-modal-close"
                                @click="requestClose"
                            >
                                <SvgIcon
                                    name="system-close"
                                    class="h-5 w-5 text-gray-900"
                                />
                            </button>
                        </div>

                        <!--
                            Sub-header (persistent, sits below the header). Keyed like the body: it is
                            where a composer that must not scroll away lives, so a record change has to
                            mount a fresh one here too. `contentKey` only ever moves on a retaining
                            modal, so this is inert on the default path.
                        -->
                        <div
                            v-if="$slots['sub-header']"
                            :key="contentKey"
                            class="shrink-0 border-b px-4 py-3 sm:px-6"
                        >
                            <slot
                                name="sub-header"
                                :form-id="formId"
                            />
                        </div>

                        <!-- Scrollable content -->
                        <div
                            :key="contentKey"
                            class="flex-1 scrollbar-gutter-stable overflow-y-auto px-4 py-4 sm:px-6"
                        >
                            <slot :form-id="formId" />
                        </div>

                        <!-- Footer -->
                        <div
                            v-if="$slots.footer"
                            class="shrink-0 px-4 py-4 sm:px-6"
                        >
                            <slot
                                name="footer"
                                :form-id="formId"
                            />
                        </div>

                        <ModalDiscardConfirm
                            :open="confirmOpen"
                            @keep="keepEditing"
                            @discard="confirmDiscard"
                        />
                    </div>
                </DialogContent>
            </Transition>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import { DialogContent, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';
import { useRetainedModalContent } from '@composables/useRetainedModalContent';
import ModalDiscardConfirm from './ModalDiscardConfirm.vue';

const props = withDefaults(
    defineProps<{
        variant?: 'center' | 'side';
        size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';
        title?: string;
        closeButton?: boolean;
        nonModal?: boolean;
        persistent?: boolean;
        fixedHeight?: boolean;
        /**
         * Retention (CU-86d3wvwq7): click-outside hides the modal and keeps its contents mounted;
         * the X becomes the only discard control and warns when there are unsaved changes.
         */
        retainOnDismiss?: boolean;
        /** Identity of the record the retained contents belong to; a change discards them. */
        retainKey?: string | number | null;
        /** Dirty check for a host that owns the form outside this modal's slot. */
        isDirty?: (() => boolean) | null;
    }>(),
    {
        variant: 'center',
        size: 'lg',
        closeButton: true,
        nonModal: false,
        persistent: false,
        fixedHeight: false,
        retainOnDismiss: false,
        retainKey: null,
        isDirty: null,
    },
);

const emit = defineEmits<{
    'modal:dismiss': [];
    'modal:discard': [];
}>();

const open = defineModel<boolean>({ required: true });

const { unmountWhenHidden, contentKey, confirmOpen, dismiss, requestClose, keepEditing, confirmDiscard, onEscape } =
    useRetainedModalContent({
        open: () => open.value,
        setOpen: (value) => {
            open.value = value;
        },
        cancel: () => {
            open.value = false;
        },
        retainOnDismiss: () => props.retainOnDismiss,
        retainKey: () => props.retainKey,
        isDirty: () => props.isDirty,
        emit,
    });

/**
 * Both reka and the discard confirm (HeadlessUI) listen for Escape on `window`, so the order is
 * registration order and reka — mounted first — wins: the keypress is claimed here and routed
 * (confirm open → keep editing, retaining → dismiss, else the plain close), and HeadlessUI's
 * listener then sees it `defaultPrevented` and stands down. The guard is for the reverse case, an
 * outer HeadlessUI dialog that registered before this one and has already answered.
 */
function onEscapeKeyDown(event: Event) {
    if (props.retainOnDismiss && event.defaultPrevented) {
        return;
    }

    event.preventDefault();
    onEscape();
}

const formId = useId();

const isNonModal = computed(() => props.nonModal || props.variant === 'side');
const showOverlay = computed(() => !isNonModal.value && props.variant === 'center');

const shouldLockScroll = computed(() => open.value && !isNonModal.value);
useScrollLock(computed(() => (shouldLockScroll.value ? document.body : null)));

function openModal() {
    open.value = true;
}

function onOverlayClick() {
    if (props.persistent) {
        return;
    }

    if (props.retainOnDismiss) {
        dismiss();

        return;
    }

    open.value = false;
}

const sizeClass = computed(() => {
    const sizes: Record<string, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        '4xl': 'max-w-4xl',
        '5xl': 'max-w-5xl',
        '6xl': 'max-w-6xl',
        full: 'max-w-full',
    };
    return sizes[props.size] ?? 'max-w-lg';
});

const transitionName = computed(() => (props.variant === 'side' ? 'mp-slide' : 'mp-center'));

const contentClasses = computed(() => {
    const base = 'fixed inset-0 z-50 flex flex-col bg-white shadow-muval-3 focus:outline-hidden w-full';

    if (props.variant === 'side') {
        return [base, sizeClass.value, 'md:inset-4 md:left-auto md:rounded-muval-3'];
    }

    return [
        base,
        sizeClass.value,
        'md:right-auto md:bottom-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-muval-2',
        props.fixedHeight ? 'md:h-[85vh]' : 'md:max-h-[85vh]',
    ];
});
</script>

<style scoped>
/* Overlay fade */
.mp-overlay-enter-active,
.mp-overlay-leave-active {
    transition: opacity 0.3s ease;
}
.mp-overlay-enter-from,
.mp-overlay-leave-to {
    opacity: 0;
}

/*
 * Center variant: fade + scale on desktop. Tailwind v4 applies the dialog's
 * `-translate-*` centering through the individual `translate` property, so the
 * transition must not repeat that translation in `transform`.
 */
.mp-center-enter-active {
    transition:
        opacity 0.3s ease-out,
        transform 0.3s ease-out;
}
.mp-center-leave-active {
    transition:
        opacity 0.2s ease-in,
        transform 0.2s ease-in;
}
.mp-center-enter-from,
.mp-center-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

/* Side variant: slide from right on desktop */
.mp-slide-enter-active {
    transition: transform 0.3s ease-out;
}
.mp-slide-leave-active {
    transition: transform 0.2s ease-in;
}
.mp-slide-enter-from,
.mp-slide-leave-to {
    transform: translateX(100%);
}

/* Mobile: both variants slide up from bottom */
@media (max-width: 767px) {
    .mp-center-enter-from,
    .mp-center-leave-to {
        opacity: 1;
        transform: translateY(100%);
    }

    .mp-slide-enter-from,
    .mp-slide-leave-to {
        transform: translateY(100%);
    }
}
</style>
